import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import { JwtModule } from '@auth0/angular-jwt';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AuthModule } from './modules/auth/auth.module';
import { DefaultRouteComponent } from './modules/auth/containers/default-route/default-route.component';
import { AdminGuard } from './modules/auth/services/admin.guard';
import { AuditorGuard } from './modules/auth/services/auditor.guard';
import { AuthGuard } from './modules/auth/services/auth.guard';
import { AuthService } from './modules/auth/services/auth.service';
import { CustomerGuard } from './modules/auth/services/customer.guard';
import { FullLayoutComponent } from './modules/customer-shared/components/full-layout/full-layout.component';
import { CustomerSharedModule } from './modules/customer-shared/customer-shared.module';
import { SharedModule } from './modules/shared/shared.module';
import { PreloadSelectedModulesList } from './preloading';
import { metaReducers, reducers } from './store';
import { AnonymousReportEffects } from './store/anonymous-report/anonymous-report.effects';
import { AuditsEffects } from './store/audits';
import { AuthEffects } from './store/auth';
import { ReportPageEffects } from './store/report';
import { UiEffects } from './store/ui';
import { LocationsModuleComponent } from './modules/customer-shared/containers/locations-module/locations-module.component';
import { UserModuleComponent } from './modules/customer-shared/containers/user-module/user-module.component';
import { ChunkErrorHandler } from './modules/shared/services/chunk-error.service';
import { ImageBrowserEffects } from './store/image-browser';
import * as Sentry from '@sentry/angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TRANSLATE_HTTP_LOADER_CONFIG, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { UserSettingsService } from './modules/shared/services/user-settings.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CustomerSchedulingEffects } from './store/customer-scheduling';
import { routerReducer, StoreRouterConnectingModule } from "@ngrx/router-store";

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    SharedModule,
    CustomerSharedModule,
    RouterModule.forRoot(
      [
        {
          // default customer module
          path: '',
          component: FullLayoutComponent,
          children: [
            {
              path: 'locations/:locationId',
              component: LocationsModuleComponent,
              canActivate: [AuthGuard, CustomerGuard],
              children: [
                {
                  path: 'analytics',
                  loadChildren: () => import('./modules/resource-center/analytics.module').then(mod => mod.AnalyticsRoutesModule),
                  // canActivate: [AuthGuard, CustomerGuard],
                },
                {
                  path: 'employees',
                  loadChildren: () => import('./modules/resource-center/employees.module').then(mod => mod.EmployeeRoutesModule),
                  // canActivate: [AuthGuard, CustomerGuard],
                },
                {
                  path: 'audits',
                  loadChildren: () => import('./modules/audits/audits.module').then(mod => mod.AuditsModule),
                  canActivate: [AuthGuard, CustomerGuard],
                  data: {
                    // preload: true,
                  },
                },
                {
                  path: 'overview',
                  loadChildren: () => import('./modules/locations/locations.module').then(mod => mod.LocationsModule),
                  canActivate: [AuthGuard, CustomerGuard],
                  data: {
                    // preload: true,
                  },
                },

                {
                  path: 'audits',
                  children: [
                    {
                      path: '',
                      loadChildren: () => import('./modules/report/report.module').then(mod => mod.ReportModule),
                      canActivate: [AuthGuard, CustomerGuard],
                      data: {
                        // preload: true,
                      },
                    },
                  ],
                },
                {
                  path: 'cctv',
                  loadChildren: () => import('./modules/cctv/cctv.module').then(mod => mod.CctvModule),
                  canActivate: [AuthGuard, CustomerGuard],
                },
                {
                  path: 'signal-center',
                  loadChildren: () =>
                    import('./modules/signal-center/customer/customer-signal-center.module').then(mod => mod.CustomerSignalCenterModule),
                  canActivate: [AuthGuard, CustomerGuard],
                },
                { path: 'history', redirectTo: 'signal-center', pathMatch: 'full' },
                {
                  path: '',
                  component: DefaultRouteComponent,
                  pathMatch: 'full',
                  data: {
                    redirectTo: ['overview'],
                  },
                },
              ],
            },
            {
              path: 'user',
              component: UserModuleComponent,
              loadChildren: () => import('./modules/user/user.module').then(mod => mod.UserModule),
              canActivate: [AuthGuard],
            },
            {
              path: '',
              component: DefaultRouteComponent,
              pathMatch: 'full',
              canActivate: [AuthGuard],
              data: {
                redirectTo: ['overview'],
              },
            },
          ],
        },

        {
          path: 'lr',
          loadChildren: () => import('./modules/locations/locations.token.module').then(mod => mod.LocationsTokenModule),
        },

        {
          path: 'r',
          loadChildren: () => import('./modules/report/report.token.module').then(mod => mod.ReportTokenModule),
        },
        {
          path: 'admin',
          loadChildren: () => import('./modules/admin/admin.module').then(mod => mod.AdminModule),
          canActivate: [AdminGuard],
        },
        {
          path: 'auditing',
          loadChildren: () => import('./modules/auditing/auditing.module').then(mod => mod.AuditingModule),
          canActivate: [AuditorGuard],
        },
        {
          path: 'onboarding',
          loadChildren: () => import('./modules/onboarding/onboarding.module').then(mod => mod.OnboardingModule),
        },

        { path: '**', component: DefaultRouteComponent, pathMatch: 'full', canActivate: [AuthGuard] },
      ],
      {
    preloadingStrategy: PreloadSelectedModulesList
},
    ),
    StoreModule.forRoot(
      {
        ...reducers,
        router: routerReducer,
      },
      {
        metaReducers,
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
        },
      }
    ),
    StoreRouterConnectingModule.forRoot(),
    EffectsModule.forRoot([
      AuthEffects,
      AuditsEffects,
      ReportPageEffects,
      AnonymousReportEffects,
      UiEffects,
      ImageBrowserEffects,
      CustomerSchedulingEffects,
    ]),
    AuthModule,

    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    JwtModule.forRoot({
      config: {
        tokenGetter: AuthService.getJwt,
        authScheme: AuthService.getScheme,
        allowedDomains: environment.jwtWhitelistedDomains,
      },
    }),
    TranslateModule.forRoot({
      fallbackLang: 'en',
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader,
      },
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !!environment.pushVapidPublicKey,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    PreloadSelectedModulesList,
    Title,
    { provide: ErrorHandler, useClass: ChunkErrorHandler },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: LOCALE_ID,
      deps: [UserSettingsService],
      useFactory: (settingsService: UserSettingsService) => settingsService.getSelectedLanguage(),
    },
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: '/assets/i18n/',
        suffix: '.json',
      },
    },
  ],

  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(trace: Sentry.TraceService) {}
}
